/**
 * theme-contracts.ts
 *
 * W3C Design Tokens Community Group (DTCG) Contracts (Sprint THEME-02):
 * - Token primitive schemas ($value, $type, $description).
 * - Semantic color & geometry token interfaces.
 * - Theme definitions, tenant overrides, density modes, and WCAG contrast evaluations.
 */

export type TokenType =
  | 'color'
  | 'dimension'
  | 'fontFamily'
  | 'fontWeight'
  | 'number'
  | 'shadow';

export interface DtcgToken<T = string> {
  readonly $value: T;
  readonly $type: TokenType;
  readonly $description?: string;
}

export type DensityMode = 'comfortable' | 'compact' | 'high_density';

export interface SemanticColorTokens {
  readonly surfaceCanvas: DtcgToken<string>;
  readonly surfacePanel: DtcgToken<string>;
  readonly surfacePanelSubtle: DtcgToken<string>;
  readonly borderSubtle: DtcgToken<string>;
  readonly borderStrong: DtcgToken<string>;
  readonly textPrimary: DtcgToken<string>;
  readonly textSecondary: DtcgToken<string>;
  readonly textMuted: DtcgToken<string>;
  readonly brandPrimary: DtcgToken<string>;
  readonly brandPrimaryHover: DtcgToken<string>;
  readonly brandOnPrimary: DtcgToken<string>;
  readonly accentSuccess: DtcgToken<string>;
  readonly accentWarning: DtcgToken<string>;
  readonly accentDanger: DtcgToken<string>;
  readonly focusRing: DtcgToken<string>;
}

export interface SemanticGeometryTokens {
  readonly radiusCard: DtcgToken<string>;
  readonly radiusControl: DtcgToken<string>;
  readonly fontSans: DtcgToken<string>;
  readonly fontMono: DtcgToken<string>;
}

export interface ThemeDefinition {
  readonly themeId: string;
  readonly displayName: string;
  readonly isDark: boolean;
  readonly colors: SemanticColorTokens;
  readonly geometry: SemanticGeometryTokens;
}

export interface TenantThemeOverride {
  readonly brandPrimaryHex?: string;
  readonly brandOnPrimaryHex?: string;
  readonly surfaceCanvasHex?: string;
  readonly surfacePanelHex?: string;
  readonly radiusCardPx?: number;
  readonly radiusControlPx?: number;
  readonly density?: DensityMode;
}

export interface ContrastEvaluation {
  readonly foregroundToken: string;
  readonly backgroundToken: string;
  readonly ratio: number;
  readonly rating: 'pass_aaa' | 'pass_aa' | 'fail';
  readonly minimumRequired: number;
}

export interface ThemeValidationReport {
  readonly isValid: boolean;
  readonly contrastEvaluations: readonly ContrastEvaluation[];
  readonly validationErrors: readonly string[];
}

export const FUNCTIONAL_QR_RENDER_POLICY = {
  foreground: '#000000',
  background: '#FFFFFF',
  quietZoneModules: 4,
  decorationAllowed: false,
  minimumContrastRatio: 7,
} as const;

export interface AccessibilityThemePolicy {
  readonly minimumNormalTextContrast: 4.5;
  readonly minimumLargeTextContrast: 3.0;
  readonly minimumUiComponentContrast: 3.0;
  readonly enforceReducedMotion: boolean;
  readonly allowDecorativeGlow: boolean;
  readonly highContrastFallbackThemeId: string;
}

export interface StandaloneExportPolicy {
  readonly defaultLocale: string;
  readonly includedLocales: readonly string[];
  readonly defaultThemeId: string;
  readonly includedThemeIds: readonly string[];
  readonly allowRuntimeThemeSwitching: boolean;
  readonly allowRuntimeLocaleSwitching: boolean;
}

